import { ROLES, hasPermission } from './rolePermissions';
import { canAccessTool, canAccessToolsHub } from './toolAccess';

/**
 * ONE ANSWER TO "SHOULD THIS MENU ITEM BE VISIBLE".
 *
 * Sidebar and TopNav carried line-for-line identical copies of this filter,
 * SettingsLayout a third with its own `adminOnly` key, and quickLinkModules a
 * fourth expressed as inline booleans. Four copies of a rule is four chances for
 * the menu to disagree with the route it points at.
 *
 * ── `roles` now bypasses for admin, and that is a fix ───────────────────────
 *
 * `RoleBasedRoute allowedRoles` has always let an admin through; the nav filters
 * did not, so a `roles:` list silently meant something different in the menu than
 * on the route it pointed at. The bypass here makes the two agree.
 *
 * That is a widening, so it needed a check: the one item it would have changed is
 * `/my-team`, and hiding THAT from admins turned out to be correct — the page
 * reads `user.departmentId` and titles itself "your department", which an admin
 * does not have. So it now says `hideFromAdmin` and keeps its behaviour, stated
 * as an intent rather than resting on a bypass that was missing by accident.
 *
 * Every predicate is ANDed, and each is skipped when the item does not declare
 * it — an item with no gate is visible to everyone, deliberately.
 */
export function navVisible(item, user) {
  if (!item) return false;
  const role = user?.role;
  const isAdmin = role === ROLES.ADMIN;

  // Admin-only, the explicit form. SettingsLayout's key, now available to all.
  if (item.adminOnly && !isAdmin) return false;

  /**
   * The opposite, and rarer: a link an admin should NOT see because the page is
   * meaningless to them, not because they lack the right. `My Team` reads
   * `user.departmentId` and titles itself "your department"; an admin has none.
   *
   * Explicit because the admin bypass below is otherwise unconditional, and
   * "hidden from admin" is a real intent that should not be spelled as an
   * accidental side effect of a role list.
   */
  if (item.hideFromAdmin && isAdmin) return false;

  /**
   * Client mail: admins and the DPM coordinator department, main or second
   * department. Not expressible as a role — the session carries the server's answer.
   */
  if (item.clientMail && !user?.clientMailAccess) return false;

  // Role membership — admin passes, matching RoleBasedRoute.
  if (item.roles && !isAdmin && !item.roles.includes(role)) return false;

  // The Tools hub link, shown only if any tool is reachable.
  if (item.toolsHub && !canAccessToolsHub(role)) return false;

  /**
   * INVERTED on purpose: `hideIfTool` hides a fallback link from people who have
   * the real thing. `/support` disappears once you can open `/support-center`.
   */
  if (item.hideIfTool && canAccessTool(role, item.hideIfTool)) return false;

  if (item.toolKey && !canAccessTool(role, item.toolKey)) return false;

  // Permission may be a single key or an array; an array is ANY-of, matching
  // `RoleBasedRoute requiredPermissions` with its default `requireAll: false`.
  if (item.permission && !hasPermission(user, item.permission)) return false;

  return true;
}

/** Filter a nav section's items, dropping sections that end up empty. */
export function visibleSections(sections, user) {
  return sections
    .map((s) => ({ ...s, items: (s.items || []).filter((i) => navVisible(i, user)) }))
    .filter((s) => s.items.length);
}
